/**
 * P2's three session endpoints, wrapped over `useSession().request()` — the one authenticated
 * door (`web/src/auth/context.ts`).
 *
 * **This module implements no revoke.** Seams §6 is explicit that the Devices page is a *client*
 * of `DELETE /users/me/sessions*`; a second revoke path would be a second security surface. All
 * three functions here shape a path and read a response, exactly as `screens/shared/api.ts` does
 * for P3/P4/P5 — and they live beside the screen rather than in that file because it is P7's and
 * this PRD extends P7's screens without taking their files (PRD §8 rule 1).
 *
 * **`SessionView` is an allow-list on both sides.** `toSessionView` on the server
 * (`packages/functions/auth/sessions.ts`) already omits every binding field; this reader names the
 * six it renders and drops the rest, so a field added to the server projection cannot arrive on
 * this screen by default either. §13 "Settings → Devices" asserts *"no binding material in any
 * response"*, and two allow-lists is what makes that survive a later widening at one end.
 */

import { buildPath, ROUTES } from '@selogate/core'
import type { ApiRequest, ApiResponse } from '../../auth/transport'
import { field, stringField } from '../../auth/transport'

export type ApiRequestFn = (request: ApiRequest) => Promise<ApiResponse>

export type SessionsResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly status: number; readonly code?: string }

/**
 * One row of Settings → Devices (§7.11).
 *
 * `deviceLabel`/`platform` are optional because the server's own projection makes them optional —
 * a session recorded before the user-agent parse existed, or one whose header was absent, has
 * neither, and the screen renders a neutral label rather than an empty row.
 */
export interface DeviceSession {
  readonly sessionId: string
  readonly deviceLabel?: string
  readonly platform?: string
  readonly firstSeenAt: string
  readonly lastSeenAt: string
  readonly isCurrent: boolean
}

function fail<T>(response: ApiResponse): SessionsResult<T> {
  const code = stringField(response.body, 'code')
  return { ok: false, status: response.status, ...(code === undefined ? {} : { code }) }
}

function isSuccess(status: number): boolean {
  return status >= 200 && status < 300
}

/**
 * Defensive, like every other reader in the app: a row missing an id or either timestamp is
 * dropped rather than rendered half-blank or thrown over. A malformed response degrades this
 * screen; it does not crash the shell around it.
 */
function readSession(raw: unknown): DeviceSession | undefined {
  const sessionId = stringField(raw, 'sessionId')
  const firstSeenAt = stringField(raw, 'firstSeenAt')
  const lastSeenAt = stringField(raw, 'lastSeenAt')
  if (sessionId === undefined || firstSeenAt === undefined || lastSeenAt === undefined) return undefined
  const deviceLabel = stringField(raw, 'deviceLabel')
  const platform = stringField(raw, 'platform')
  return {
    sessionId,
    ...(deviceLabel === undefined ? {} : { deviceLabel }),
    ...(platform === undefined ? {} : { platform }),
    firstSeenAt,
    lastSeenAt,
    isCurrent: field(raw, 'isCurrent') === true,
  }
}

/** `GET /users/me/sessions` — the account's live sessions, already sorted most-recent-first. */
export async function listSessions(request: ApiRequestFn): Promise<SessionsResult<readonly DeviceSession[]>> {
  const response = await request({
    method: ROUTES.sessionList.method,
    path: buildPath(ROUTES.sessionList),
  })
  if (!isSuccess(response.status)) return fail(response)
  const raw = field(response.body, 'sessions')
  const rows = Array.isArray(raw)
    ? raw.map(readSession).filter((row): row is DeviceSession => row !== undefined)
    : []
  return { ok: true, data: rows }
}

/**
 * `DELETE /users/me/sessions/{sessionId}`.
 *
 * **Revoking the current session is a sign-out** (§9) — but that path does not come through here:
 * the screen routes it to `SessionManager.signOut()`, which performs the same revoke *and* the
 * local teardown. This function is for the other rows.
 */
export async function revokeDeviceSession(
  request: ApiRequestFn,
  sessionId: string,
): Promise<SessionsResult<void>> {
  const response = await request({
    method: ROUTES.sessionRevoke.method,
    path: buildPath(ROUTES.sessionRevoke, { sessionId }),
  })
  if (!isSuccess(response.status)) return fail(response)
  return { ok: true, data: undefined }
}

/** `DELETE /users/me/sessions?others=true` — "revoke all other devices". This one survives. */
export async function revokeOtherDeviceSessions(request: ApiRequestFn): Promise<SessionsResult<number>> {
  const response = await request({
    method: ROUTES.sessionRevokeAll.method,
    path: `${buildPath(ROUTES.sessionRevokeAll)}?others=true`,
  })
  if (!isSuccess(response.status)) return fail(response)
  const revoked = field(response.body, 'revoked')
  return { ok: true, data: typeof revoked === 'number' ? revoked : 0 }
}
